import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { workCategories, categoryOverviews, brandWorks } from "../../mock/mock";

// Auto-cycling slideshow card used for brand tiles
function SlideshowCard({ item }) {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!item.images || item.images.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % item.images.length), 3000);
    return () => clearInterval(t);
  }, [item.images]);

  const handleClick = () => {
    if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4 }}
      onClick={handleClick}
      className={`group relative rounded-2xl overflow-hidden border border-white/5 bg-[#0c0e0c] ${item.url ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {/* Drive embed iframe */}
        {item.videoEmbed ? (
          <iframe
            src={item.videoEmbed}
            className="absolute inset-0 h-full w-full"
            allow="autoplay"
            frameBorder="0"
            allowFullScreen
          />
        ) : item.video ? (
          <video
            src={item.video}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          item.images.map((img, i) => (
            <img key={i} src={img} alt="" loading="lazy"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
                i === slide ? "opacity-100" : "opacity-0"
              }`}
            />
          ))
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/20" />

        {/* Slide progress dots */}
        {item.images.length > 1 && (
          <div className="absolute top-3 left-0 right-0 flex justify-center gap-[5px] z-10">
            {item.images.map((_, i) => (
              <div
                key={i}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  i === slide ? "w-5 bg-[var(--sage)]" : "w-[5px] bg-white/30"
                }`}
              />
            ))}
          </div>
        )}

        {/* Tag chip */}
        <div className="absolute top-4 left-4 z-10">
          <span className="chip">{item.tag}</span>
        </div>

        {/* External link button — only for web items */}
        {item.url && (
          <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center group-hover:bg-[var(--sage)] group-hover:border-[var(--sage)] transition-colors z-10">
            <ExternalLink size={15} className="text-white group-hover:text-black transition-colors" />
          </div>
        )}

        {/* Text content */}
        <div className="absolute left-0 right-0 bottom-0 p-5 z-10">
          <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--sage)]">{item.type}</div>
          <h3 className="font-display text-[22px] leading-tight mt-1">{item.brand}</h3>
          <p className="text-[13px] text-[var(--text-dim)] mt-1 leading-[1.5]">{item.desc}</p>
          {item.metric && (
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
              <span className="mono text-[11px] text-[var(--text-dim)]">Result</span>
              <span className="mono text-[12px] text-[var(--text)]">{item.metric}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Category overview tile for "All Work" tab — clicking switches to that tab
function CategoryTile({ cat, onSelect }) {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!cat.images || cat.images.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % cat.images.length), 2500);
    return () => clearInterval(t);
  }, [cat.images]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4 }}
      onClick={() => onSelect(cat.id)}
      className="group relative rounded-2xl overflow-hidden border border-white/5 bg-[#0c0e0c] cursor-pointer"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {cat.images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt=""
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
              i === slide ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/15 group-hover:from-black/80 transition-all duration-500" />

        {/* Slide dots */}
        {cat.images.length > 1 && (
          <div className="absolute top-3 left-0 right-0 flex justify-center gap-[5px] z-10">
            {cat.images.map((_, i) => (
              <div
                key={i}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  i === slide ? "w-5 bg-[var(--sage)]" : "w-[5px] bg-white/30"
                }`}
              />
            ))}
          </div>
        )}

        {/* Arrow icon */}
        <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center group-hover:bg-[var(--sage)] group-hover:border-[var(--sage)] transition-colors z-10">
          <ArrowUpRight size={16} className="text-white group-hover:text-black transition-colors" />
        </div>

        {/* Text */}
        <div className="absolute left-0 right-0 bottom-0 p-5 z-10">
          <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--sage)]">{cat.count}</div>
          <h3 className="font-display text-[26px] leading-tight mt-1">{cat.label}</h3>
          <p className="text-[13px] text-[var(--text-dim)] mt-1 leading-[1.5]">{cat.desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Work() {
  const [active, setActive] = useState("all");

  return (
    <section id="work" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10">
          <div>
            <div className="section-label mb-6">Selected Work</div>
            <h2 className="font-display text-[42px] md:text-[64px] leading-[1.02] tracking-[-0.02em] max-w-3xl">
              Where <span className="italic sage-text">creativity</span> meets function.
            </h2>
          </div>
          <p className="max-w-sm text-[15px] leading-[1.7] text-[var(--text-dim)]">
            A tight edit of recent projects across performance marketing, brand design, web and content.
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex flex-wrap gap-2 mb-10">
          {workCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 rounded-full border transition-colors ${
                active === c.id
                  ? "border-[var(--sage)] text-[var(--sage)] bg-[var(--sage)]/10"
                  : "border-white/10 text-[var(--text-dim)] hover:text-[var(--text)] hover:border-white/25"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          {active === "all" ? (
            <motion.div
              key="all"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {categoryOverviews.map((cat) => (
                <CategoryTile key={cat.id} cat={cat} onSelect={setActive} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {(brandWorks[active] || []).map((item) => (
                <SlideshowCard key={item.id} item={item} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <div className="mt-12 flex justify-center">
          <a href="#contact" className="btn-ghost">
            Discuss your project <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
